import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { PageHeader, LoadingState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Car, Motorcycle, ChartLine, TrendUp, Clock, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444'];

export default function ParkingAnalytics() {
    const [overview, setOverview] = useState(null);
    const [trends, setTrends] = useState([]);
    const [peakHours, setPeakHours] = useState([]);
    const [loading, setLoading] = useState(true);
    const [trendDays, setTrendDays] = useState("30");

    async function loadAnalytics() {
        setLoading(true);
        try {
            const [overviewRes, trendsRes, peakRes] = await Promise.all([
                api.get("/vehicles/analytics/overview"),
                api.get(`/vehicles/analytics/trends?days=${trendDays}`),
                api.get("/vehicles/analytics/peak-hours")
            ]);
            
            setOverview(overviewRes.data);
            setTrends(trendsRes.data);
            setPeakHours(peakRes.data);
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadAnalytics();
        // Auto-refresh every 60 seconds
        const interval = setInterval(loadAnalytics, 60000);
        return () => clearInterval(interval);
    }, [trendDays]);

    if (loading || !overview) return <LoadingState />;

    const slotUtilizationData = [
        { name: "Covered", value: overview.parking.covered },
        { name: "Open", value: overview.parking.open },
        { name: "Visitor", value: overview.parking.visitor }
    ].filter(d => d.value > 0);

    const vehicleTypeData = [
        { name: "2-Wheeler", value: overview.vehicles.two_wheeler },
        { name: "4-Wheeler", value: overview.vehicles.four_wheeler }
    ].filter(d => d.value > 0);

    return (
        <div className="animate-fade-up">
            <PageHeader
                overline="Analytics"
                title="Parking & Vehicle Analytics"
                subtitle="Comprehensive insights into parking utilization and vehicle activity"
                actions={
                    <Button onClick={loadAnalytics} variant="outline" className="rounded-full">
                        Refresh Data
                    </Button>
                }
            />

            {/* Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {/* Occupancy Rate */}
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-3">
                        <CheckCircle size={32} weight="fill" />
                        <span className="text-5xl font-bold">{overview.parking.occupancy_rate}%</span>
                    </div>
                    <p className="text-green-100 text-sm">Parking Occupancy</p>
                    <p className="text-xs text-green-200 mt-1">
                        {overview.parking.occupied} of {overview.parking.total_slots} slots occupied
                    </p>
                </div>

                {/* Total Vehicles */}
                <div className="bg-white border-2 border-brand-line rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                            <Car size={24} className="text-blue-600" weight="fill" />
                        </div>
                        <div>
                            <p className="text-3xl font-heading font-bold text-brand-ink">{overview.vehicles.approved}</p>
                            <p className="text-sm text-brand-inkSoft">Registered Vehicles</p>
                        </div>
                    </div>
                    <div className="flex gap-4 mt-3 pt-3 border-t border-brand-line text-xs">
                        <div>
                            <Motorcycle size={16} className="inline text-blue-600" /> {overview.vehicles.two_wheeler} 2W
                        </div>
                        <div>
                            <Car size={16} className="inline text-purple-600" /> {overview.vehicles.four_wheeler} 4W
                        </div>
                    </div>
                </div>

                {/* Active Vehicles */}
                <div className="bg-white border-2 border-brand-line rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                            <TrendUp size={24} className="text-green-600" weight="fill" />
                        </div>
                        <div>
                            <p className="text-3xl font-heading font-bold text-brand-ink">{overview.activity.active_vehicles}</p>
                            <p className="text-sm text-brand-inkSoft">Vehicles Inside</p>
                        </div>
                    </div>
                    <p className="text-xs text-brand-inkSoft mt-3 pt-3 border-t border-brand-line">
                        {overview.activity.today_logs} entries today
                    </p>
                </div>

                {/* Avg Duration */}
                <div className="bg-white border-2 border-brand-line rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                            <Clock size={24} className="text-purple-600" weight="fill" />
                        </div>
                        <div>
                            <p className="text-3xl font-heading font-bold text-brand-ink">{overview.activity.avg_duration_hours}h</p>
                            <p className="text-sm text-brand-inkSoft">Avg Duration</p>
                        </div>
                    </div>
                    <p className="text-xs text-brand-inkSoft mt-3 pt-3 border-t border-brand-line">
                        Based on last 7 days
                    </p>
                </div>
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Daily Trends */}
                <div className="bg-white border border-brand-line rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="font-heading text-lg font-semibold text-brand-ink flex items-center gap-2">
                                <ChartLine size={20} />
                                Daily Activity Trends
                            </h3>
                            <p className="text-sm text-brand-inkSoft">Entry & exit patterns</p>
                        </div>
                        <Select value={trendDays} onValueChange={setTrendDays}>
                            <SelectTrigger className="w-32">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="7">Last 7 days</SelectItem>
                                <SelectItem value="30">Last 30 days</SelectItem>
                                <SelectItem value="90">Last 90 days</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={trends}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis 
                                dataKey="date" 
                                tick={{ fontSize: 12 }}
                                tickFormatter={(date) => new Date(date).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                            />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip 
                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                                labelFormatter={(date) => new Date(date).toLocaleDateString()}
                            />
                            <Legend />
                            <Line type="monotone" dataKey="entries" stroke="#10b981" strokeWidth={2} name="Entries" />
                            <Line type="monotone" dataKey="exits" stroke="#3b82f6" strokeWidth={2} name="Exits" />
                            <Line type="monotone" dataKey="unique_vehicles" stroke="#8b5cf6" strokeWidth={2} name="Unique Vehicles" />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* Peak Hours */}
                <div className="bg-white border border-brand-line rounded-lg p-6">
                    <div className="mb-4">
                        <h3 className="font-heading text-lg font-semibold text-brand-ink flex items-center gap-2">
                            <Clock size={20} />
                            Peak Hours Analysis
                        </h3>
                        <p className="text-sm text-brand-inkSoft">Hourly entry/exit distribution (Last 30 days)</p>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={peakHours}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="hour" tick={{ fontSize: 11 }} interval={2} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip 
                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                            />
                            <Legend />
                            <Bar dataKey="entries" fill="#10b981" name="Entries" />
                            <Bar dataKey="exits" fill="#ef4444" name="Exits" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Charts Row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Slot Utilization */}
                <div className="bg-white border border-brand-line rounded-lg p-6">
                    <div className="mb-4">
                        <h3 className="font-heading text-lg font-semibold text-brand-ink">Slot Distribution by Category</h3>
                        <p className="text-sm text-brand-inkSoft">Total slot allocation</p>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={slotUtilizationData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {slotUtilizationData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                    {slotUtilizationData.length === 0 && (
                        <p className="text-center text-sm text-brand-inkSoft -mt-40" data-testid="slot-distribution-empty">No parking slots yet</p>
                    )}
                </div>

                {/* Vehicle Type Distribution */}
                <div className="bg-white border border-brand-line rounded-lg p-6">
                    <div className="mb-4">
                        <h3 className="font-heading text-lg font-semibold text-brand-ink">Vehicle Type Distribution</h3>
                        <p className="text-sm text-brand-inkSoft">Approved vehicles by type</p>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={vehicleTypeData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {vehicleTypeData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                    {vehicleTypeData.length === 0 && (
                        <p className="text-center text-sm text-brand-inkSoft -mt-40" data-testid="vehicle-type-empty">No approved vehicles yet</p>
                    )}
                </div>
            </div>

            {/* Insights */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="font-heading text-lg font-semibold text-blue-900 mb-3">📊 Key Insights</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="bg-white rounded-sm p-3">
                        <p className="font-medium text-brand-ink mb-1">Occupancy Status</p>
                        <p className="text-brand-inkSoft">
                            {overview.parking.occupancy_rate > 80 
                                ? "⚠️ High occupancy - Consider expanding parking" 
                                : overview.parking.occupancy_rate > 50 
                                ? "✅ Healthy occupancy rate" 
                                : "✅ Plenty of parking available"}
                        </p>
                    </div>
                    <div className="bg-white rounded-sm p-3">
                        <p className="font-medium text-brand-ink mb-1">Peak Time</p>
                        <p className="text-brand-inkSoft">
                            {peakHours.length > 0 && (() => {
                                const peak = peakHours.reduce((max, hour) => hour.total > max.total ? hour : max, peakHours[0]);
                                return `🕐 Busiest hour: ${peak.hour} (${peak.total} activities)`;
                            })()}
                        </p>
                    </div>
                    <div className="bg-white rounded-sm p-3">
                        <p className="font-medium text-brand-ink mb-1">Pending Actions</p>
                        <p className="text-brand-inkSoft">
                            {overview.vehicles.pending > 0 
                                ? `⚠️ ${overview.vehicles.pending} vehicles awaiting approval` 
                                : "✅ All approvals up to date"}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
